import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Building2, Wifi, Package, Gauge, Battery, Shield } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RobotRecommendationWidget from "@/components/RobotRecommendationWidget";
import RobotPageNav from "@/components/RobotPageNav";
import flashbotMaxImg from "@/assets/robots/flashbot-max-new.jpeg";

const FlashBotMax = () => {
  const specifications = [
    {
      icon: <Building2 className="w-5 h-5" />,
      label: "Navigation",
      value: "VSLAM+ Semi-Outdoor"
    },
    {
      icon: <Package className="w-5 h-5" />,
      label: "Compartments",
      value: "2-4 Modular"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Path Clearance",
      value: "60cm Narrow Corridors"
    },
    {
      icon: <Battery className="w-5 h-5" />,
      label: "Runtime",
      value: "9 Hours"
    }
  ];

  const keyFeatures = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "VSLAM+ Semi-Outdoor Delivery",
      description: "Advanced visual SLAM navigation enables reliable autonomous operation in complex semi-open areas including outdoor pathways and multi-building environments."
    },
    {
      icon: <Wifi className="w-6 h-6" />,
      title: "Full-Scope IoT Integration",
      description: "Seamless integration with elevators, turnstiles, and access control systems enables autonomous multi-floor delivery without human intervention."
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Modular Compartment System",
      description: "Flexible 2-4 adjustable compartments with ventilation options accommodate various delivery needs from food to packages and supplies."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "60cm Path Clearance",
      description: "Compact design navigates narrow corridors and tight spaces effortlessly, making it ideal for building delivery where space is limited."
    },
    {
      icon: <Battery className="w-6 h-6" />,
      title: "Extended 9-Hour Runtime",
      description: "All-day operation capability with 4-hour fast charging and automatic charging dock return ensures continuous availability."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Multi-Compartment Security",
      description: "Advanced security with password, phone verification, and NFC access ensures safe delivery of valuable items and maintains chain of custody."
    }
  ];

  const applications = [
    {
      title: "Office Buildings",
      description: "Multi-floor mail delivery, supply distribution, and inter-department transport with automatic elevator control and floor navigation."
    },
    {
      title: "Hotels & Resorts",
      description: "Guest room service, amenity delivery, and laundry transport across multiple floors and buildings with secure compartment access."
    },
    {
      title: "Hospitals & Healthcare",
      description: "Medicine delivery, lab sample transport, and supply distribution between departments and floors with secure access control."
    },
    {
      title: "Corporate Campuses",
      description: "Inter-building delivery, document transport, and supply distribution across large campus environments with outdoor navigation."
    },
    {
      title: "Residential Buildings",
      description: "Package delivery to residents, mail distribution, and food delivery with secure access control and multi-floor capability."
    },
    {
      title: "Educational Institutions",
      description: "Supply delivery, equipment transport, and inter-building logistics across campus with autonomous navigation and security."
    }
  ];

  return (
    <>
      <SEO
        title="FlashBot Max - Building Delivery Expert | Neil AI"
        description="Advanced building delivery robot with VSLAM+ semi-outdoor navigation, full IoT integration, and modular compartments for seamless multi-floor operations."
        keywords="FlashBot Max, building delivery, multi-floor delivery, IoT robot, elevator robot, autonomous delivery, office robot"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <RobotRecommendationWidget />
        
        <main className="container mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-12">
          <RobotPageNav />

          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div className="space-y-6">
              <div>
                <Badge className="mb-4">Building Delivery Expert</Badge>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">
                  FlashBot Max
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Advanced building delivery robot with VSLAM+ semi-outdoor navigation, full IoT integration, and modular compartments for seamless multi-floor operations.
                </p>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <span className="text-xs font-semibold">{spec.label}</span>
                      </div>
                      <p className="text-xs md:text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button asChild size="lg" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="flex-1">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg" className="flex-1">
                  <a href="tel:+13369624539">
                    Call Us: (336) 962-4539
                  </a>
                </Button>
              </div>

              {/* ROI Highlight */}
              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold mb-1 text-sm md:text-base">ROI Impact</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Reduce delivery labor costs by 70% while improving delivery speed and reliability. Typical ROI within 12-18 months through automation of multi-floor deliveries.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden bg-white/5 flex items-center justify-center p-4 md:p-8">
                <img 
                  src={flashbotMaxImg} 
                  alt="FlashBot Max - Building Delivery Expert"
                  className="w-full h-full object-cover"
                />
              </div>
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">
                NEW
              </Badge>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-4">
              <TabsTrigger value="overview" className="text-xs md:text-sm">Overview</TabsTrigger>
              <TabsTrigger value="specs" className="text-xs md:text-sm">Specs</TabsTrigger>
              <TabsTrigger value="applications" className="text-xs md:text-sm">Applications</TabsTrigger>
              <TabsTrigger value="accessories" className="text-xs md:text-sm">Add-Ons</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Key Features</CardTitle>
                  <CardDescription>
                    Revolutionary building delivery with IoT integration
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {keyFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 md:gap-4">
                        <div className="text-primary flex-shrink-0">{feature.icon}</div>
                        <div>
                          <h4 className="font-bold mb-1 md:mb-2 text-sm md:text-base">{feature.title}</h4>
                          <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Navigation System</span>
                      <span className="text-sm md:text-base">VSLAM+ Semi-Outdoor</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Compartments</span>
                      <span className="text-sm md:text-base">2-4 Modular with Ventilation</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Path Clearance</span>
                      <span className="text-sm md:text-base">60cm Narrow Corridor Navigation</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Runtime</span>
                      <span className="text-sm md:text-base">9 Hours / 4-Hour Charging</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">IoT Integration</span>
                      <span className="text-sm md:text-base">Elevator, Turnstile, Access Control</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Security</span>
                      <span className="text-sm md:text-base">Password, Phone, NFC Verification</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      <span className="font-semibold text-sm md:text-base">Charging</span>
                      <span className="text-sm md:text-base">Automatic Dock Return</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Ideal Applications</CardTitle>
                  <CardDescription>
                    Perfect for multi-floor and multi-building environments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-lg border border-[hsl(var(--line))] hover:border-primary/50 transition-colors">
                        <h4 className="font-bold mb-2 text-sm md:text-base">{app.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="accessories" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">FlashBot Vending Machine Module</CardTitle>
                  <CardDescription>
                    Transform your FlashBot into a mobile retail and vending solution
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-6">
                    <div>
                      <h3 className="font-bold text-base md:text-lg mb-3 text-primary">Key Features</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Package className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm">Modular vending compartments with secure access control</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Shield className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm">Multiple payment options: QR code, NFC, and mobile payment integration</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Wifi className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm">Cloud-based inventory management and real-time stock monitoring</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Shield className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm">Temperature control options for food, beverages, and sensitive items</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Battery className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm">Autonomous restocking alerts and route optimization</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-bold text-base md:text-lg mb-3 text-primary">Applications</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm"><strong>Office Buildings:</strong> Snacks, beverages, office supplies on-demand delivery to desks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm"><strong>Hotels:</strong> Mini-bar replenishment, grab-and-go items, amenity sales to guest rooms</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm"><strong>Airports:</strong> Travel essentials, snacks, souvenirs at gates and lounges</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm"><strong>Hospitals:</strong> Pharmacy items, patient care products, cafeteria items</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs md:text-sm"><strong>Universities:</strong> Campus-wide vending service, textbooks, study supplies</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-3 md:p-4 rounded-lg bg-primary/10 border border-primary/30">
                    <h4 className="font-bold mb-2 flex items-center gap-2 text-sm md:text-base">
                      <Package className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                      Revenue Enhancement
                    </h4>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      The vending module transforms FlashBot into a mobile revenue generator. Increase sales by bringing products directly to customers at their location, reduce labor costs with autonomous operation, and capture impulse purchases with strategic positioning throughout your facility.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6 text-center">
                  <h3 className="font-orbitron text-lg md:text-xl font-bold mb-2 md:mb-3">Interested in the Vending Module?</h3>
                  <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">
                    Contact us to learn more about adding the vending module to your FlashBot Max configuration.
                  </p>
                  <Button asChild size="lg">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule Consultation
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Final CTA */}
          <Card className="glass-effect border-primary/30 bg-primary/5 mt-8 md:mt-12">
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="font-orbitron font-bold text-xl md:text-2xl mb-4">
                Ready to Revolutionize Building Delivery?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Transform multi-floor and multi-building operations with FlashBot Max's advanced IoT integration and autonomous navigation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule a Demo
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/robot-recommender">
                    Find Your Perfect Robot
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FlashBotMax;
